import axios from "axios";
import { isNative, getAccessToken, refreshMobileTokens, clearTokens } from "./mobileAuth";
export const API = `${process.env.REACT_APP_BACKEND_URL || "https://tricomas.it"}/api`;
export const api = axios.create({ baseURL: API, withCredentials: !isNative(), timeout: 20000 });
export const WHATSAPP_NUM = "393715233268";

// M4-A4 — Bearer mobile (solo Capacitor). Web invariato: withCredentials cookie httpOnly.
api.interceptors.request.use(async (config) => {
  if (isNative()) {
    const t = await getAccessToken();
    if (t) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${t}`;
    }
  }
  return config;
});

// Su 401 (solo mobile): refresh UNA sola volta (single-flight) poi retry; se fallisce → logout locale + /accedi.
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    const status = error?.response?.status;
    if (!isNative() || status !== 401 || !original || original._retry) {
      return Promise.reject(error);
    }
    const url = original.url || "";
    if (url.includes("/auth/refresh/mobile") || url.includes("/auth/login/mobile")) {
      return Promise.reject(error);
    }
    original._retry = true;
    try {
      const newAccess = await refreshMobileTokens();
      original.headers = original.headers || {};
      original.headers.Authorization = `Bearer ${newAccess}`;
      return api(original);
    } catch (e) {
      await clearTokens();
      if (window.location.pathname !== "/accedi") {
        window.location.assign("/accedi");
      }
      return Promise.reject(error);
    }
  },
);

export function apiErr(e) {
  if (e?.message === "secure_storage_error" || e?.message === "secure_storage_timeout")
    return "Impossibile completare l'accesso sul dispositivo. Riprova.";
  if (e?.message === "Network Error") return "Connessione assente. Controlla la rete e riprova.";
  if (e?.response?.status === 429) {
    const ra = e?.response?.data?.retry_after;
    return ra ? `Troppe richieste. Riprova tra circa ${ra} second${ra === 1 ? "o" : "i"}.` : "Troppe richieste. Attendi qualche minuto e riprova.";
  }
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Si è verificato un errore. Riprova.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  if (d && typeof d === "object") return d.message || d.detail || d.msg || JSON.stringify(d);
  return String(d);
}

export const track = (event, meta = {}) => { api.post(`/track/${event}`, meta).catch(() => {}); };

// M4-A5.1 — origin_url per Stripe Checkout.
// Web: window.location.origin (INVARIATO). Capacitor: dominio HTTPS canonico tricomas.it
// (MAI capacitor://localhost) → success_url/cancel_url puntano a pagine web reali su tricomas.it.
export const MOBILE_WEB_ORIGIN = "https://tricomas.it";
export function checkoutOriginUrl() {
  return isNative() ? MOBILE_WEB_ORIGIN : window.location.origin;
}

// Naviga verso Stripe Checkout nel contesto TOP-LEVEL.
// L'app può girare dentro un iframe (Preview Emergent) e Stripe rifiuta il framing
// (frame-ancestors 'none'): con window.location.href resterebbe una pagina bianca.
// target="_top" esce dall'iframe; compatibile con Safari iOS, tricomas.it e PWA.
// M4-A5.1 — Su Capacitor apre il Checkout nel BROWSER DI SISTEMA (@capacitor/browser),
// mai dentro la WebView.
export function goToCheckout(url) {
  if (!url) return false;
  if (isNative()) {
    import("@capacitor/browser")
      .then(({ Browser }) => Browser.open({ url }))
      .catch(() => { try { window.open(url, "_system"); } catch (_) { /* noop */ } });
    return true;
  }
  try {
    const a = document.createElement("a");
    a.href = url;
    a.target = "_top";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } catch (e) {
    try { (window.top || window).location.href = url; }
    catch (_) { window.location.href = url; }
  }
  return true;
}

// Formattazione date per la UI (presentazione). Il DB/API resta ISO YYYY-MM-DD.
export const fmtDate = (iso) => {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(iso);
};
export const fmtDateLong = (iso) => {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso));
  if (!m) return String(iso);
  try { return new Date(`${m[1]}-${m[2]}-${m[3]}T12:00:00`).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" }); }
  catch (e) { return `${m[3]}/${m[2]}/${m[1]}`; }
};
// Normalizza intervalli orari per la UI: "10:00 17:00" / "10:00-17:00" -> "10:00–17:00".
export const fmtTimeRange = (t) => {
  if (!t) return "";
  const s = String(t).trim();
  const m = /(\d{1,2}[:.]\d{2})\s*[-–—aeto ]+\s*(\d{1,2}[:.]\d{2})/i.exec(s);
  if (m) return `${m[1].replace(".", ":")}–${m[2].replace(".", ":")}`;
  return s;
};
// Messaggi disponibilità lato artista (la nota Admin resta privata).
export const availLabel = (state, stationType) => {
  if (state === "COMPLETO") return stationType === "cabina" ? "Cabina Privacy già prenotata in questa data." : "Postazioni Open Space già prenotate in questa data.";
  return state; // DISPONIBILE / ULTIMA POSTAZIONE invariati
};

// text already contains the link; do NOT append url again (fixes double-link bug)
export async function shareText(title, text) {
  const { shareLink } = await import("./mobileFiles");
  return shareLink({ title, text });
}
